import { type SearchImagesQuery, useSearchImagesInfiniteQuery } from '@vizit/api';
import { useInfiniteScroll } from '@vizit/hooks';
import { debounce } from '@vizit/utils';
import * as React from 'react';
import { transformStateToQueryParams, useAssetTable } from '../../providers';
import { calculateOptimalLoadCount } from './asset-grid-viewer.utils';

/**
 * Hook to calculate optimal initial load count based on viewport size
 * Optimized to avoid extra renders and debounce resize events
 */
function useOptimalInitialLoadCount(): number {
  // Initialize with calculated value to avoid extra render
  const [loadCount, setLoadCount] = React.useState(() => calculateOptimalLoadCount());

  React.useEffect(() => {
    // Debounced resize handler to limit calculations during window resizing
    const debouncedHandleResize = debounce(() => {
      const newCount = calculateOptimalLoadCount();
      setLoadCount(newCount);
    }, 250); // 250ms debounce

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  return loadCount;
}

interface UseAssetGridViewerProps {
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
}

/**
 * Hook to fetch portfolio assets for the asset grid viewer with infinite loading
 * Uses the generated getImages query internally and automatically calculates optimal initial load count
 */
function useAssetGridViewer(props: UseAssetGridViewerProps) {
  const { useOptimalInitialLoad = true } = props;

  const { portfolioId, limit, sortBy, sortDirection, productCategoryId, imageType } = useAssetTable();

  const optimalInitialCount = useOptimalInitialLoadCount();

  // Use optimal count for initial load, standard limit for subsequent pages
  const effectiveInitialLimit = useOptimalInitialLoad ? optimalInitialCount : limit;

  const filterQueryParams = React.useMemo(
    () =>
      transformStateToQueryParams({
        limit,
        sortBy,
        sortDirection,
        productCategoryId,
        imageType,
      }),
    [limit, sortBy, sortDirection, productCategoryId, imageType],
  );

  const { data, isLoading, error, hasNextPage, hasPreviousPage, isFetchingNextPage, fetchNextPage } =
    useSearchImagesInfiniteQuery({
      portfolio_id: portfolioId ?? '',
      source: undefined,
      exclude_source: ['generated'],
      parent_id: undefined,
      ...filterQueryParams,
    });

  const assets = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchImagesQuery[] } | undefined;
    const allImages = infiniteData?.pages?.flatMap((page) => page.data) || [];
    return allImages;
  }, [data]);

  const totalCount = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchImagesQuery[] } | undefined;
    return infiniteData?.pages?.[0]?.total_count ?? 0;
  }, [data]);

  const sentinelRef = useInfiniteScroll({ hasNextPage: hasNextPage ?? false, isFetchingNextPage, fetchNextPage });

  // Use hasPreviousPage to determine loading count
  // hasPreviousPage = false: initial load (show optimal count)
  // hasPreviousPage = true: subsequent loads (show standard limit)
  const loadingCount = React.useMemo(() => {
    if (isLoading && !hasPreviousPage) {
      // Initial load: show optimal initial count skeletons (e.g., 24)
      return effectiveInitialLimit;
    }
    if (isFetchingNextPage) {
      // Subsequent loads: show standard limit skeletons (e.g., 12)
      return limit;
    }
    // Not loading: no skeletons needed
    return 0;
  }, [isLoading, hasPreviousPage, isFetchingNextPage, effectiveInitialLimit, limit]);

  return {
    assets,
    totalCount,
    isLoading,
    hasError: !!error,
    hasNextPage,
    isFetchingNextPage,
    sentinelRef,
    loadingCount,
  };
}

export { useAssetGridViewer };
export type { UseAssetGridViewerProps };
